<script>
export default {
  props: {
    loading: {
      type:    Boolean,
      default: true,
    },
    // How to size and position the loading indicator - supports three modes:
    // 'content' - the content area only (not side nav or header)
    // 'main' - entire main view excluding the header, but including the side nav
    // 'relative' - content up to the nearest relatively positioned element
    mode: {
      type:    String,
      default: 'content',
    },
    noDelay: {
      type:    Boolean,
      default: false,
    }
  },

  data() {
    return { timer: null, showMessage: this.noDelay };
  },

  mounted() {
    this.timer = setTimeout(() => {
      this.showMessage = true;
    }, 250);
  },

  beforeUnmount() {
    clearTimeout(this.timer);
  }
};
</script>

<template>
  <div
    v-if="loading"
    class="loading-indicator"
    role="status"
    aria-live="polite"
    aria-busy="true"
  >
    <div
      v-if="showMessage"
      class="overlay"
      :class="{ 'overlay-content-mode' : mode === 'content', 'overlay-main-mode' : mode === 'main' }"
    >
      <span
        class="loading-spinner"
        aria-hidden="true"
      />
      <t
        k="generic.loading"
        :raw="true"
      />
    </div>
  </div>
  <div v-else>
    <slot />
  </div>
</template>

<style lang="scss" scoped>
  .loading-spinner {
    width: 28px;
    height: 28px;
    border: 3px solid var(--border);
    border-top-color: var(--primary);
    border-radius: 50%;
    animation: loading-turn 900ms linear infinite;
  }

  @keyframes loading-turn {
    to { transform: rotate(360deg); }
  }

  @media (prefers-reduced-motion: reduce) {
    .loading-spinner { animation: none; }
  }

  .overlay {
    align-items: center;
    background-color: var(--subtle-overlay-bg);
    display: flex;
    flex-direction: column;
    gap: 12px;
    color: var(--muted);
    font-size: 13px;
    justify-content: center;
    position: absolute;
    bottom: 0;
    top: 0;
    left: 0;
    right: 0;
    text-align: center;

    // Covers both default `content` mode, an often used `relative` mode and any other value of mode
    z-index: z-index('loading');

    &-main-mode {
      left: calc(var(--app-nav-width, 0px) + var(--layout-wm-left, 0px));
      right: var(--layout-wm-right, 0px);
      bottom: var(--wm-height, 0px);
      top: var(--header-height);
      z-index: z-index('loadingMain');
    }

    &-content-mode {
      // Home and plain layouts have no secondary navigation column.
      left: calc(var(--app-nav-width, 0px) + var(--layout-wm-left, 0px));
      right: var(--layout-wm-right, 0px);
      bottom: var(--wm-height, 0px);
      top: calc(var(--header-height) + var(--mobile-nav-height, 0px));
    }
  }

  // Only reserve the secondary column when that navigation is rendered.
  :global(.dashboard-content:has(> .default-side-nav) .overlay-content-mode) {
    left: calc(var(--nav-width, 0px) + var(--app-nav-width, 0px) + var(--layout-wm-left, 0px));
  }
</style>
